











































































































































































import { Vue, Component, Prop } from 'vue-property-decorator'
import moment from 'moment'
import i18nService from "@/services/i18n"
import { formatDigitsGrouping } from "../../../common/utils/formattingUtils"
import { Option } from "../../../common/types/Option"
import { BudgetType, MeasureUnit } from "../../../common/types/roots/fields"
import {
    SvodMbTableData,
    SvodMbRow,
    SvodMbIncomeValue,
    SvodMbExpenseValue,
} from "../table-data/SvodMbTableData"
import { SvodMbIndicator, SvodMbIndicatorsState } from "../types/SvodMbIndicators"
import { SvodMbBlockDef, SvodMbColumnDef, svodMbIncomeBlocks, svodMbExpenseBlocks } from "../svod-mb"

type SvodMbPart = 'incomes' | 'expenses'

/** Метаданные строки боковика, вычисленные на фронте (RQ.20: уровень ВЗК; подписи group/total). */
interface SvodMbRowMeta {
    /** Ключ подписи под table.<labelKey> - только для group/total (RQ.05 раскладка наименований) */
    labelKey?: string
    /** Ключ уровня ВЗК под table.vzk_tooltip.<vzkLevelKey> - для node и части total (RQ.20) */
    vzkLevelKey?: 'level_node' | 'level_total_region'
}

@Component({})
export default class ReportSvodMb extends Vue {
    @Prop({ required: true }) tableData!: SvodMbTableData
    @Prop({ required: true }) indicators!: SvodMbIndicatorsState
    /** Дата фильтра (YYYY-MM-DD) - данные соответствуют ЭТОЙ дате, в тексте показывается +1 день (RQ.36) */
    @Prop({ default: null }) date!: string | null
    @Prop({ default: null }) region!: Option<string> | null
    @Prop({ default: () => [] }) budgetTypes!: Option<BudgetType>[]
    @Prop({ default: null }) measureUnit!: Option<MeasureUnit> | null
    @Prop({ default: true }) roundUp!: boolean
    @Prop({ default: 0 }) roundUpTo!: number | null

    /** RQ.32: активная часть отчета, по умолчанию "Доходы" */
    public part: SvodMbPart = 'incomes'

    /** RQ.34: состояние развернутости узлов - общее для обеих частей, по умолчанию все свернуты */
    public expandedNodes: Record<number, boolean> = {}

    // ==============================================================================================
    // Локализация / базовые геттеры
    // ==============================================================================================

    private get locale(): string {
        return i18nService.locale
    }

    /** Метод-обертка для $t внутри узла svod_mb.table (относительный путь начинается с "../" уходит на уровень svod_mb) */
    public tr(key: string): string {
        if (key.startsWith('../')) {
            const base = 'modules.budget_execution_monitoring.reports_constructor'
            return String(this.$t(`${base}.${key.substring(3)}`))
        }
        const base = 'modules.budget_execution_monitoring.reports_constructor.svod_mb.table'
        return String(this.$t(`${base}.${key}`))
    }

    private optionLabel(option: Option<any> | null): string {
        if (!option) return ''
        if (this.locale === 'kk' && option.labelKk) return option.labelKk
        return option.label
    }

    public get measureUnitLabel(): string {
        return this.optionLabel(this.measureUnit)
    }

    // ==============================================================================================
    // Даты (RQ.36/RQ.38)
    // ==============================================================================================

    public get displayDatePlusOneFull(): string {
        if (!this.date) return ''
        return moment(this.date).add(1, 'day').format('DD.MM.YYYY')
    }

    public get displayDatePlusOneShort(): string {
        if (!this.date) return ''
        const suffix = this.locale === 'kk' ? ' ж.' : ' г.'
        return moment(this.date).add(1, 'day').format('DD.MM.YY') + suffix
    }

    /** RQ.38: если фактическая дата хотя бы одной из форм отличается от даты фильтра - предупреждение с перечнем форм */
    public get actualDateWarning(): string | null {
        const actualDates = this.tableData?.actual_dates
        if (!actualDates || !this.date) return null

        const forms: { value: string | null, labelKey: string }[] = [
            { value: actualDates.form_219, labelKey: 'warning.form_219' },
            { value: actualDates.form_420, labelKey: 'warning.form_420' },
            { value: actualDates.form_534, labelKey: 'warning.form_534' },
        ]

        const diffParts: string[] = []
        for (const form of forms) {
            if (form.value === this.date) continue
            const valueText = form.value ? moment(form.value).format('DD.MM.YYYY') : this.tr('warning.no_data_for_form')
            diffParts.push(`${this.tr(form.labelKey)} — ${valueText}`)
        }

        if (diffParts.length === 0) return null
        return `${this.tr('warning.prefix')} ${diffParts.join(', ')}`
    }

    // ==============================================================================================
    // Переключатель частей (RQ.32)
    // ==============================================================================================

    public get partOptions(): { value: SvodMbPart, text: string }[] {
        return [
            { value: 'incomes', text: this.tr('../incomes') },
            { value: 'expenses', text: this.tr('../expenses') },
        ]
    }

    public get partLabel(): string {
        return this.part === 'incomes' ? this.tr('../incomes') : this.tr('../expenses')
    }

    // ==============================================================================================
    // Колонки/блоки (RQ.08, EAK-1188: поколоночные показатели вместо 5 обобщенных ключей каркаса)
    // ==============================================================================================

    private get blocksDef(): SvodMbBlockDef[] {
        return this.part === 'incomes' ? svodMbIncomeBlocks : svodMbExpenseBlocks
    }

    /** Блоки, которые нужно показать в панели "Скрыть показатели" (все, независимо от активности) */
    public get activeBlocksDef(): SvodMbBlockDef[] {
        return this.blocksDef
    }

    private get activeIndicatorsList(): SvodMbIndicator[] {
        return this.part === 'incomes' ? this.indicators.incomes : this.indicators.expenses
    }

    public indicatorObject(key: string): SvodMbIndicator {
        const found = this.activeIndicatorsList.find(it => it.key === key)
        if (found) return found
        // Защита от рассинхронизации набора показателей с версией шаблона (например, старый сохраненный шаблон) -
        // создаем временный объект, чтобы не падать в рантайме; в indicators он не персистится.
        const fallback: SvodMbIndicator = { key, active: true }
        this.activeIndicatorsList.push(fallback)
        return fallback
    }

    private isColumnActive(key: string): boolean {
        return this.indicatorObject(key).active
    }

    /** RQ.08: минимум один показатель в части должен оставаться активным - блокируем снятие последнего */
    public isLastActiveIndicator(key: string): boolean {
        const indicator = this.indicatorObject(key)
        if (!indicator.active) return false
        return this.activeIndicatorsList.filter(it => it.active).length <= 1
    }

    public columnLabel(column: SvodMbColumnDef): string {
        return this.tr('columns.' + column.labelKey).replace('$date', this.displayDatePlusOneShort)
    }

    /** Блоки с отфильтрованными по активности колонками (для заголовков/тела таблицы), пустые блоки не выводятся */
    public get blocksWithActiveColumns(): SvodMbBlockDef[] {
        return this.blocksDef
            .map(block => ({ ...block, columns: block.columns.filter(c => this.isColumnActive(c.key)) }))
            .filter(block => block.columns.length > 0)
    }

    /** +1 - колонка-"стрелка" разворачивания узла, +1 - колонка "Наименование" */
    public get totalColumnCount(): number {
        return 2 + this.blocksWithActiveColumns.reduce((sum, b) => sum + b.columns.length, 0)
    }

    // ==============================================================================================
    // Дерево (RQ.34)
    // ==============================================================================================

    public get hasRows(): boolean {
        return Array.isArray(this.tableData?.rows) && this.tableData.rows.length > 0
    }

    private get rows(): SvodMbRow[] {
        return this.tableData?.rows ?? []
    }

    private get childrenByParent(): Map<number, SvodMbRow[]> {
        const map = new Map<number, SvodMbRow[]>()
        for (const row of this.rows) {
            if (row.parent_idx === null || row.parent_idx === undefined) continue
            if (!map.has(row.parent_idx)) map.set(row.parent_idx, [])
            map.get(row.parent_idx)!.push(row)
        }
        return map
    }

    public hasChildren(row: SvodMbRow): boolean {
        if (row.row_type !== 'node') return false
        return (this.childrenByParent.get(row.idx)?.length ?? 0) > 0
    }

    public isExpanded(idx: number): boolean {
        return !!this.expandedNodes[idx]
    }

    public toggleNode(idx: number): void {
        this.$set(this.expandedNodes, idx, !this.isExpanded(idx))
    }

    private get expandableRows(): SvodMbRow[] {
        return this.rows.filter(row => this.hasChildren(row))
    }

    /** Кнопка "развернуть/свернуть всё" в шапке показывается, только если в дереве есть узлы с детьми */
    public get hasAnyExpandable(): boolean {
        return this.expandableRows.length > 0
    }

    public get allExpanded(): boolean {
        return this.hasAnyExpandable && this.expandableRows.every(row => this.isExpanded(row.idx))
    }

    public toggleAll(): void {
        const expand = !this.allExpanded
        for (const row of this.expandableRows) {
            this.$set(this.expandedNodes, row.idx, expand)
        }
    }

    /**
     * Видимые строки боковика. Группы/узлы/итоги видны всегда, строки бюджетов (дети узла)
     * видны только если узел-родитель развернут. Дерево общее для обеих частей (RQ.34).
     * Строка бюджета БЕЗ родителя - самостоятельная верхнеуровневая строка (выбор конкретного
     * бюджета region-фильтром - "собственно областной"/"собственно районный"/МСУ напрямую, без
     * захода через иерархию области), под развертывание не подпадает и видна всегда - иначе
     * отчет по такому региону выглядит пустым (нет узла-родителя, который можно развернуть).
     */
    public get visibleRows(): SvodMbRow[] {
        return this.rows.filter(row => {
            if (row.row_type !== 'budget') return true
            if (row.parent_idx === null || row.parent_idx === undefined) return true
            return this.isExpanded(row.parent_idx)
        })
    }

    // ==============================================================================================
    // Подписи строк (RQ.05: group/total - из i18n фронта, node/budget - из ответа бэкенда)
    // ==============================================================================================

    /**
     * Метаданные строк (подпись group/total, уровень ВЗК) вычисляются один раз по всему боковику:
     * 1-я встреченная строка group - "Районы", 2-я - "Города" (порядок гарантирован бэкендом:
     * report_svod_mb.py сначала добавляет группу районов, затем городов, если они есть);
     * среди строк total последняя по порядку - всегда "Всего по области" (она добавляется в конце
     * безусловно), предпоследняя с непустым code - "Собственно областной" (ее code = код облбюджета),
     * иначе - "Итого по райгородам".
     */
    private get rowsMeta(): Map<number, SvodMbRowMeta> {
        const meta = new Map<number, SvodMbRowMeta>()

        // Подпись строк с фиксированным наименованием берется по label_key, который присылает
        // бэкенд (RQ.05: за подписи таблицы отвечает экран, поэтому текст - из i18n фронта, а
        // бэкенд лишь помечает, ЧТО это за строка). Позиционно эти строки определять нельзя:
        // состав групп переменный (RQ.12 - заголовок пустой группы не выводится).
        // Уровень ВЗК для подсказки (RQ.20): узлы и "Итого по райгородам" - "между районом и МСУ",
        // "Всего по области" - "между областью и районами, между районом и МСУ".
        for (const row of this.rows) {
            if (row.row_type === 'node') {
                meta.set(row.idx, { vzkLevelKey: 'level_node' })
                continue
            }
            if (row.row_type !== 'group' && row.row_type !== 'total') continue

            const labelKey = row.label_key ?? undefined
            const vzkLevelKey = labelKey === 'total_region'
                ? 'level_total_region'
                : (labelKey === 'total_districts_cities' ? 'level_node' : undefined)
            meta.set(row.idx, { labelKey, vzkLevelKey })
        }
        return meta
    }

    public rowLabel(row: SvodMbRow): string {
        const meta = this.rowsMeta.get(row.idx)
        if (meta?.labelKey) return this.tr(meta.labelKey)
        return this.locale === 'kk' ? (row.name_kk || row.name_ru) : row.name_ru
    }

    /**
     * Отступ названия по уровню вложенности. Узлы районов/городов (row.level = 1) выровнены
     * по базовому отступу с итоговыми строками (row.level = 0) - визуально они воспринимаются
     * как один верхний уровень дерева, а не как вложенный уровень под итогами; более глубокая
     * вложенность (развернутые строки бюджетов) продолжает отступать относительно них.
     */
    public nameIndent(row: SvodMbRow): string {
        return (0.75 + Math.max(row.level - 1, 0) * 1.25) + 'rem'
    }

    // ==============================================================================================
    // Значения ячеек (RQ.21: % и отклонение считаются на клиенте; RQ.40: округление отображения)
    // ==============================================================================================

    private get incomesByIdx(): Map<number, SvodMbIncomeValue> {
        const map = new Map<number, SvodMbIncomeValue>()
        for (const v of this.tableData?.incomes ?? []) map.set(v.idx, v)
        return map
    }

    private get expensesByIdx(): Map<number, SvodMbExpenseValue> {
        const map = new Map<number, SvodMbExpenseValue>()
        for (const v of this.tableData?.expenses ?? []) map.set(v.idx, v)
        return map
    }

    private getValueEntry(idx: number): SvodMbIncomeValue | SvodMbExpenseValue | undefined {
        return this.part === 'incomes' ? this.incomesByIdx.get(idx) : this.expensesByIdx.get(idx)
    }

    /** Разрядность отображения: выключенный свитч округления трактуется как 5 знаков (RQ.40) */
    private get effectiveDecimals(): number {
        return this.roundUp ? (this.roundUpTo ?? 0) : 5
    }

    private getRawCellValue(row: SvodMbRow, column: SvodMbColumnDef): number | null {
        const entry: any = this.getValueEntry(row.idx)
        if (!entry) return null

        if (column.kind === 'raw') {
            if (!column.valueField) return null
            // null/undefined (например, rest_balancing не рассчитан) - пустая ячейка, а не 0
            const raw = entry[column.valueField]
            return raw === null || raw === undefined ? null : Number(raw)
        }
        if (column.kind === 'sum') {
            return (column.sumFields ?? []).reduce((sum, field) => sum + Number(entry[field] ?? 0), 0)
        }
        if (column.kind === 'deviation') {
            const plan = Number(entry[column.planField as string] ?? 0)
            const fact = Number(entry[column.factField as string] ?? 0)
            return plan - fact
        }
        if (column.kind === 'percent') {
            const plan = Number(entry[column.planField as string] ?? 0)
            const fact = Number(entry[column.factField as string] ?? 0)
            // RQ.21: при нулевом плане ячейка ПУСТАЯ (не 0 и не прочерк)
            if (plan === 0) return null
            return (fact / plan) * 100
        }
        return null
    }

    public formatCell(row: SvodMbRow, column: SvodMbColumnDef): string {
        const value = this.getRawCellValue(row, column)
        if (value === null || value === undefined || Number.isNaN(value)) return ''

        const decimals = column.kind === 'percent' ? 2 : this.effectiveDecimals
        return formatDigitsGrouping(value.toFixed(decimals))
    }

    // ==============================================================================================
    // Подсказка об исключенных ВЗК (RQ.20)
    // ==============================================================================================

    /** RQ.20: разделитель разрядов - НЕРАЗРЫВНЫЙ пробел, десятичный - запятая (отдельно от формата ячеек таблицы) */
    private formatVzkAmount(value: number, decimals: number): string {
        const fixed = value.toFixed(decimals)
        const [wholePart, decimalPart] = fixed.split('.')
        const grouped = wholePart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        return decimalPart ? `${grouped},${decimalPart}` : grouped
    }

    public vzkTooltipText(row: SvodMbRow): string | null {
        const meta = this.rowsMeta.get(row.idx)
        if (!meta?.vzkLevelKey) return null

        const entry: any = this.getValueEntry(row.idx)
        const amount = Number(entry?.vzk_excluded ?? 0)
        if (!amount) return null

        const levelText = this.tr('vzk_tooltip.' + meta.vzkLevelKey)
        const sumText = this.formatVzkAmount(amount, this.effectiveDecimals)
        return this.tr('vzk_tooltip.text')
            .replace('$level', levelText)
            .replace('$sum', sumText)
            .replace('$unit', this.measureUnitLabel)
    }
}
